import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import {
  Button,
  Collapse,
  Modal,
  Form,
  Input,
  Select,
  Table,
  Upload,
  Space,
  Popconfirm,
  Typography,
  Spin,
  message,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import {
  listFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  uploadFAQDocx,
  downloadFAQTemplate,
  FAQItem,
} from '../services/faqService';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 預設類別選項
const CATEGORY_OPTIONS = [
  { value: '全文審閱', label: '全文審閱' },
  { value: '使用者管理', label: '使用者管理' },
];

// 類別排序（全文審閱在前）
const CATEGORY_ORDER = ['全文審閱', '使用者管理'];

// 類別標題色系：使用 app 主色青藍
const CATEGORY_STYLE = {
  bg: '#f0f7fa',
  border: '#b8dde8',
  text: '#1E88A8',
  bar: '#1E88A8',
};

const FAQPage: React.FC = () => {
  const { user } = useAuth();
  const { faqEnabled, canEditFaq } = useFeatureFlags();
  const isAdmin = canEditFaq;

  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 新增/編輯 Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FAQItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // 上傳 Word Modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState<FAQItem[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  const loadFAQs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFAQs();
      setFaqs(data);
    } catch {
      message.error('載入常見問答失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFAQs();
  }, [loadFAQs]);

  if (!faqEnabled && !user?.is_superadmin) {
    return <Navigate to='/' replace />;
  }

  // ── 新增 / 編輯 ──────────────────────────────────────────────
  const openAddModal = () => {
    setEditTarget(null);
    form.resetFields();
    setEditModalOpen(true);
  };

  const openEditModal = (item: FAQItem) => {
    setEditTarget(item);
    form.setFieldsValue({ category: item.category, question: item.question, answer: item.answer });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const cat = values.category ?? '';
      if (editTarget) {
        await updateFAQ(editTarget.id, values.question, values.answer, cat);
        message.success('已更新');
      } else {
        await createFAQ(values.question, values.answer, cat);
        message.success('已新增');
      }
      setEditModalOpen(false);
      loadFAQs();
    } catch (err: unknown) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── 刪除 ─────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    try {
      await deleteFAQ(id);
      message.success('已刪除');
      loadFAQs();
    } catch (err: unknown) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  // ── 上傳 Word ────────────────────────────────────────────────
  const uploadProps: UploadProps = {
    accept: '.docx',
    showUploadList: false,
    beforeUpload: async file => {
      setUploadLoading(true);
      try {
        const imported = await uploadFAQDocx(file);
        setPreviewItems(imported);
        message.success(`已成功匯入 ${imported.length} 筆問答`);
        setUploadModalOpen(false);
        loadFAQs();
      } catch (err: unknown) {
        if (err instanceof Error) {
          message.error(err.message);
        }
      } finally {
        setUploadLoading(false);
      }
      return false;
    },
  };

  // ── 依類別分組（全文審閱在前）──────────────────────────────
  const grouped: { category: string; items: FAQItem[] }[] = [];
  faqs.forEach(faq => {
    const cat = faq.category || '其他';
    const existing = grouped.find(g => g.category === cat);
    if (existing) {
      existing.items.push(faq);
    } else {
      grouped.push({ category: cat, items: [faq] });
    }
  });
  // 依 CATEGORY_ORDER 排序，不在清單裡的類別排最後
  grouped.sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.category);
    const bi = CATEGORY_ORDER.indexOf(b.category);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <PageLayout currentPage='faq'>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px' }}>
        {/* 頁首 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            常見問答
          </Title>
          {isAdmin && (
            <Space wrap>
              <Button icon={<PlusOutlined />} type='primary' onClick={openAddModal}>
                新增問答
              </Button>
              <Button icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
                Word 匯入
              </Button>
            </Space>
          )}
        </div>

        {/* FAQ 清單（依類別分組） */}
        <Spin spinning={loading}>
          {faqs.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
              目前尚無常見問答
            </div>
          ) : (
            grouped.map(group => {
              const collapseItems = group.items.map((faq, idx) => ({
                key: String(faq.id),
                label: (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text strong style={{ fontSize: 15 }}>
                      Q{idx + 1}. {faq.question}
                    </Text>
                    {isAdmin && (
                      <Space
                        size='small'
                        onClick={e => e.stopPropagation()}
                        style={{ flexShrink: 0, marginLeft: 12 }}
                      >
                        <Tooltip title='編輯'>
                          <Button
                            type='text'
                            icon={<EditOutlined />}
                            size='small'
                            onClick={() => openEditModal(faq)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title='確定要刪除此問答？'
                          onConfirm={() => handleDelete(faq.id)}
                          okText='刪除'
                          cancelText='取消'
                          okButtonProps={{ danger: true }}
                        >
                          <Tooltip title='刪除'>
                            <Button type='text' icon={<DeleteOutlined />} size='small' danger />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    )}
                  </div>
                ),
                children: (
                  <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{faq.answer}</Text>
                ),
              }));

              return (
                <div key={group.category} style={{ marginBottom: 28 }}>
                  <div
                    style={{
                      background: CATEGORY_STYLE.bg,
                      border: `1px solid ${CATEGORY_STYLE.border}`,
                      borderRadius: '8px 8px 0 0',
                      padding: '10px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 4,
                        height: 18,
                        background: CATEGORY_STYLE.bar,
                        borderRadius: 2,
                        flexShrink: 0,
                      }}
                    />
                    <Text strong style={{ fontSize: 16, color: CATEGORY_STYLE.text }}>
                      {group.category}
                    </Text>
                  </div>
                  <Collapse
                    accordion={false}
                    items={collapseItems}
                    style={{
                      background: '#fff',
                      borderRadius: '0 0 8px 8px',
                      borderTop: 'none',
                    }}
                  />
                </div>
              );
            })
          )}
        </Spin>
      </div>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editTarget ? '編輯問答' : '新增問答'}
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalOpen(false)}
        okText={editTarget ? '儲存' : '新增'}
        cancelText='取消'
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout='vertical' style={{ marginTop: 16 }}>
          <Form.Item name='category' label='類別'>
            <Select
              allowClear
              showSearch
              placeholder='選擇或輸入類別（如：全文審閱、使用者管理）'
              options={CATEGORY_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            name='question'
            label='問題'
            rules={[{ required: true, message: '請輸入問題' }]}
          >
            <TextArea rows={3} placeholder='請輸入問題內容' />
          </Form.Item>
          <Form.Item name='answer' label='回答' rules={[{ required: true, message: '請輸入回答' }]}>
            <TextArea rows={6} placeholder='請輸入回答內容' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 上傳 Word Modal（含下載範本按鈕） */}
      <Modal
        title='Word 匯入'
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          setPreviewItems([]);
        }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          請上傳符合範本格式的 .docx 檔案。
        </div>
        <Button
          icon={<DownloadOutlined />}
          size='small'
          style={{ marginBottom: 16 }}
          onClick={async () => {
            try {
              await downloadFAQTemplate();
            } catch {
              message.error('下載範本失敗');
            }
          }}
        >
          Word 範本
        </Button>
        <Spin spinning={uploadLoading}>
          <Upload.Dragger {...uploadProps} style={{ padding: '8px 0' }}>
            <p className='ant-upload-drag-icon' style={{ margin: '8px 0 4px' }}>
              <InboxOutlined style={{ fontSize: 32, color: '#1677ff' }} />
            </p>
            <p className='ant-upload-text' style={{ fontSize: 14, margin: '0 0 4px' }}>
              點擊或拖曳 .docx 檔案至此
            </p>
            <p className='ant-upload-hint' style={{ fontSize: 12, margin: 0 }}>
              僅支援 Word (.docx) 格式
            </p>
          </Upload.Dragger>
        </Spin>
        {previewItems.length > 0 && (
          <Table
            style={{ marginTop: 16 }}
            size='small'
            dataSource={previewItems}
            rowKey='id'
            pagination={false}
            columns={[
              { title: '類別', dataIndex: 'category', width: 100, ellipsis: true },
              { title: '問題', dataIndex: 'question', ellipsis: true },
              { title: '回答', dataIndex: 'answer', ellipsis: true },
            ]}
          />
        )}
      </Modal>
    </PageLayout>
  );
};

export default FAQPage;
